<template>
  <div>
    <h1 class="text-center font-extrabold text-xl mb-5">
      <template v-if="edit">Modifier le profil de {{ profile.fullName }}</template>
      <template v-else>{{ $t("Add a user") }}</template>
    </h1>
    <div class="w-full">
      <div class="grid grid-cols-2 gap-x-6 gap-y-4">
        <div v-if="!edit">
          <FormField id="firstName" :label="$t('First Name')" name="firstName" required v-model="firstName" />
        </div>
        <div v-if="!edit">
          <FormField id="last_name" :label="$t('Last Name')" name="last_name" required v-model="lastName" />
        </div>
        <div v-if="!edit">
          <FormField id="email" :label="$t('Email Address')" name="email" required v-model="email" />
        </div>
        <div>
          <Selector id="role" :label="$t('Role')" name="role" v-model="role" :options="roles" label-class="mb-1" />
        </div>
        <div v-if="isRegionalManager">
          <Selector
            id="region"
            :label="$t('Region')"
            name="region"
            v-model="region"
            :options="regionOptions"
            label-class="mb-1"
          />
        </div>
      </div>

      <div v-if="!roleAdminSelected" class="mt-5">
        <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t("Selling points") }}</label>
        <CardSelector
          v-model="storeIds"
          :items="storesOptions"
          label-key="label"
          value-key="value"
          :placeholder="$t('Select selling points')"
          :max-selections="isSellerOrResponsible ? 1 : null"
        />
      </div>

      <div class="mt-5 flex justify-end">
        <Loading v-if="isLoading" />
        <GenericButton v-else @click="submit" :disabled="isRegionalManager && !region">
          {{ $t("Validate") }}
        </GenericButton>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from "vuex";
import { FormField, Selector, Loading, GenericButton, CardSelector, toCamelCase } from "@estaly/ui";

export default {
  components: {
    FormField,
    GenericButton,
    Loading,
    Selector,
    CardSelector,
  },
  props: {
    profile: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      edit: false,
      isLoading: false,
      firstName: "",
      lastName: "",
      email: "",
      storeIds: [],
      role: this.profile?.role,
      region: this.profile?.region || null,
      roles: [],
      regions: [],
      storesOptions: [],
    };
  },
  computed: {
    roleAdminSelected() {
      return this.role === "organization_admin";
    },
    isSellerOrResponsible() {
      return this.role === "seller" || this.role === "responsible";
    },
    isRegionalManager() {
      return this.role === "regional_manager";
    },
    regionOptions() {
      return this.regions.map((r) => ({ value: r, label: r }));
    },
  },
  watch: {
    role() {
      if (this.roleAdminSelected) {
        this.storeIds = [];
      } else if (this.isSellerOrResponsible && this.storeIds.length > 1) {
        this.storeIds = this.storeIds.slice(0, 1);
      }
      if (!this.isRegionalManager) {
        this.region = null;
      }
    },
    async region(newRegion) {
      if (this.isRegionalManager && newRegion) {
        await this.fetchStoresByRegion(newRegion);
      }
    },
  },
  created() {
    this.edit = !!this.profile;
    this.setupFormData();
  },
  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("merchantProfiles", {
      updateMerchantProfile: "update",
      newMerchantProfile: "new",
      createMerchantProfile: "create",
    }),
    ...mapActions("store", { fetchStoresIndex: "fetchIndex" }),
    async setupFormData() {
      this.isLoading = true;
      const response = await this.newMerchantProfile();
      this.isLoading = false;
      if (!response) return;

      const { roles, stores, regions } = response;
      this.roles = roles;
      this.regions = regions || [];
      this.storesOptions = stores.map((store) => ({
        value: store.id,
        label: store.name,
      }));
      this.role ||= this.roles[0].value;
      if (this.edit && this.isRegionalManager && this.region) {
        await this.refreshStoresForRegion(this.region);
      }
      const salesChannels = this.profile?.stores?.map((store) => store.salesChannel) || [];
      this.storeIds = this.storesOptions
        .filter((option) => salesChannels.includes(option.label))
        .map((option) => option.value);
    },
    async submit() {
      this.edit ? await this.update() : await this.create();
    },
    async update() {
      this.isLoading = true;
      const success = await this.updateMerchantProfile({
        id: this.profile.id,
        storeIds: this.storeIds,
        role: this.role,
        region: this.region,
      });
      this.isLoading = false;
      if (!success) return;

      this.notify({
        category: "simple",
        type: "success",
        title: "Mise à jour effectuée",
      });
      this.$emit("success");
    },
    async refreshStoresForRegion(region) {
      const response = await this.fetchStoresIndex({ region: [region], status: ["active"], perPage: 2000 });
      if (!response?.success) return;
      const data = toCamelCase(response.data);
      this.storesOptions = data.records.map((store) => ({
        value: store.id,
        label: store.salesChannel,
      }));
    },
    async fetchStoresByRegion(region) {
      this.storeIds = [];
      await this.refreshStoresForRegion(region);
    },
    async create() {
      this.isLoading = true;
      const success = await this.createMerchantProfile({
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        storeIds: this.storeIds,
        role: this.role,
        region: this.region,
      });
      this.isLoading = false;
      if (!success) return;

      this.notify({
        category: "simple",
        type: "success",
        title: "Utilisateur créé",
        text: "Un email d'invitation a été envoyé à l'utilisateur",
      });
      this.$emit("success");
    },
  },
};
</script>
